/*
 * "Also in this design" (blocks/design-switcher.liquid).
 * Tapping another type opens the shared quick view panel with that type's size guide, so a shopper can
 * compare and add it to the cart without leaving the product they came for. Modifier clicks and tiles
 * without a template stay plain product links.
 */
import { QuickViewPanel } from '@theme/quick-view-panel';

class DesignSwitcher extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {QuickViewPanel | null} */
  #panel = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#panel = new QuickViewPanel(this, { source: 'design-switcher' });
    this.#panel.bind(signal);

    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    // Warm the first gallery image as soon as a tile is aimed at.
    for (const type of ['pointerover', 'focusin']) {
      this.addEventListener(type, (event) => QuickViewPanel.preload(this.#template(this.#tile(event))), {
        passive: true,
        signal,
      });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#panel?.destroy();
    this.#panel = null;
  }

  /** @param {Event} event */
  #tile(event) {
    const tile = event.target instanceof Element ? event.target.closest('[data-quick-view]') : null;
    return tile instanceof HTMLAnchorElement && this.contains(tile) ? tile : null;
  }

  /** @param {HTMLElement | null} tile */
  #template(tile) {
    const template = tile?.dataset.quickView ? document.getElementById(tile.dataset.quickView) : null;
    return template instanceof HTMLTemplateElement ? template : null;
  }

  /** @param {MouseEvent} event */
  #handleClick(event) {
    const tile = this.#tile(event);
    const template = this.#template(tile);
    if (!tile || !template || !this.#panel) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    event.preventDefault();
    const guide = this.querySelector(`[data-size-guide-home="${CSS.escape(tile.dataset.guide || '')}"] .size-guide`);
    this.#panel.open(template, { url: tile.pathname, opener: tile, guide });
  }
}

if (!customElements.get('design-switcher')) customElements.define('design-switcher', DesignSwitcher);
